import {
  CondensedButton,
  Pill,
  Spinner,
  Table,
  Tooltip,
} from "@instructure/ui";
import { t } from "i18next";
import { useCallback, useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Files } from "../../../services/FilesService";
import { ReviewService } from "../../../services/ReviewService";
import { TermsContext } from "../../../utils/Context";
import { ERROR, useNotification } from "../../CustomHooks/NotificationProvider";
import { sortTermsDescending } from "../../TermManagement/utils";
import { CourseReviewContext } from "../CourseReview";
import { INITIAL_COURSE_REVIEW } from "../utils";

const PENDING = "pending";
const IN_PROGRESS = "in progress";
const PUBLISHED = "published";

const CourseReviewEntry = ({ review, status }) => {
  const showNotification = useNotification();
  const { insightsTerms } = useContext(TermsContext);
  const { setDraftData } = useContext(CourseReviewContext);
  const location = useLocation();
  const { section, cycle } = location.state;
  const navigate = useNavigate();

  const [relevantTerms, setRelevantTerms] = useState([]);
  const [draftLoading, setDraftLoading] = useState(false);

  const startYear = review.academic_years[0];
  const trueEndYear = useCallback(() => {
    for (let i = 0; i < review.academic_years.length; i++) {
      if (review.academic_years[i] === null) {
        return review.academic_years[i - 1];
      }
    }
    return review.academic_years[review.academic_years.length - 1];
  }, [review.academic_years]);
  const endYear = review.academic_years[review.academic_years.length - 1];

  useEffect(() => {
    const trueEndYearValue = trueEndYear();
    if (startYear && trueEndYearValue) {
      const startDate = new Date(startYear.start_at);
      const endDate = new Date(trueEndYearValue.end_at);
      const filteredTerms = insightsTerms.filter((term) => {
        const termDate = new Date(term.start_at);
        return termDate >= startDate && termDate <= endDate;
      });
      setRelevantTerms(sortTermsDescending(filteredTerms));
    }
  }, [insightsTerms, startYear, trueEndYear]);

  async function handleLoadReview() {
    try {
      setDraftLoading(true);
      const draft = await Files.getFileWithPresignedGetUrl(
        "response.json",
        "reviewDrafts",
        ReviewService.getReviewFolderPath(
          review.cycle.ddl_id,
          review.reviewable.ddl_id,
          review.id
        )
      );

      if (draft) {
        setDraftData(draft);
        sessionStorage.setItem(
          "courseReview/" + review.id,
          JSON.stringify(draft)
        );
      }
      navigate("/course-review/data-view", {
        state: {
          section: section,
          cycle: cycle,
          relevantTerms: relevantTerms,
          review: review,
        },
      });
    } catch (error) {
      if (error.name === "SyntaxError") {
        if (review.shareable_code) {
          const tenantId = window.location.hostname.split(".")[0];
          const courseReview = await Files.getPublicFileContent(
            "response.json",
            "public-reviews",
            ReviewService.getReviewFolderPath(
              review.cycle.ddl_id,
              review.reviewable.ddl_id,
              review.id
            ),
            tenantId === "localhost" ? "ddl-local" : tenantId
          );
          const courseReviewObj = JSON.parse(courseReview.content);
          setDraftData(courseReviewObj);

          navigate("/course-review/summary", {
            state: { section: section, cycle: cycle, review: review },
          });
        } else {
          setDraftData(INITIAL_COURSE_REVIEW);
          sessionStorage.removeItem("courseReview/" + cycle.id);
          navigate("/course-review/data-view", {
            state: {
              section: section,
              cycle: cycle,
              relevantTerms: relevantTerms,
              review: review,
            },
          });
        }
      } else {
        showNotification(
          t("notifications.general.fetchError", {
            obj: t("courseReview.courseReview"),
          }),
          ERROR
        );
      }
    } finally {
      setDraftLoading(false);
    }
  }

  function actionText(status) {
    switch (status) {
      case PENDING:
        return t("courseReview.start");
      case IN_PROGRESS:
        return t("global.continue");
      case PUBLISHED:
        return t("courseReview.view");
      default:
        return "";
    }
  }

  return (
    <Table.Row>
      <Table.Cell>
        {startYear?.name ? (
          <Pill color="info">{startYear.name}</Pill>
        ) : (
          <Pill color="warning">{t("global.na")}</Pill>
        )}
        {" - "}
        {endYear?.name ? (
          <Pill color="info">{endYear.name}</Pill>
        ) : (
          <Pill color="warning">{t("global.na")}</Pill>
        )}
      </Table.Cell>
      <Table.Cell>
        {draftLoading ? (
          <Spinner size="x-small" renderTitle={t("global.pleaseWait")} />
        ) : (
          <Tooltip
            renderTip={t("courseReview.missingAcademicYears")}
            placement="end"
            on={
              !startYear?.name || !endYear?.name ? ["hover", "focus"] : "none"
            }
          >
            <CondensedButton
              onClick={handleLoadReview}
              disabled={!startYear?.name || !endYear?.name}
            >
              {actionText(status)}
            </CondensedButton>
          </Tooltip>
        )}
      </Table.Cell>
    </Table.Row>
  );
};

export default CourseReviewEntry;
